import React, { useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileSpreadsheet,
  HelpCircle,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { IdsEcoaRow, IdsEcoaSkippedRow } from './types';
import {
  EXPECTED_HEADERS,
  finalizeMonthValue,
  finalizeYearValue,
  getMonthSelectValue,
  getRowIdentityKey,
  getYearOptions,
  getYearSelectValue,
  makeLocalGuid,
  MONTH_OPTIONS,
  normalizeHeaderKey,
  normalizeText,
  parseMonthNumber,
  parseYearNumber,
  REQUIRED_IMPORT_HEADERS,
  rowsAreEqual,
} from './utils';

type ImportRowStatus = 'valid' | 'invalid' | 'skipped';

type ParsedImportRow = IdsEcoaRow & {
  rowNumber: number;
  valid: boolean;
  status: ImportRowStatus;
  reason?: string;
};

type ImportModalProps = {
  isOpen: boolean;
  existingRows: IdsEcoaRow[];
  onClose: () => void;
  onImport: (rows: IdsEcoaRow[], skippedRows?: IdsEcoaSkippedRow[]) => Promise<boolean>;
};

const YEAR_OPTIONS = getYearOptions();

const HEADER_ALIASES: Record<keyof Omit<IdsEcoaRow, 'guid'>, string[]> = {
  idRegra: ['idregra'],
  nomePromocao: ['nomepromocao'],
  mesReferencia: ['mesreferencia', 'mesreferancia', 'mesreferencial'],
  anoReferencia: ['anoreferencia', 'anoreferancia', 'anoreferencial'],
};

function getHeaderIndex(headerIndexes: Map<string, number>, aliases: string[]) {
  for (const alias of aliases) {
    const index = headerIndexes.get(alias);
    if (index !== undefined) return index;
  }

  return undefined;
}

function isEmptyRow(row: ParsedImportRow) {
  return [
    row.idRegra,
    row.nomePromocao,
    row.mesReferencia,
    row.anoReferencia,
  ].every((value) => normalizeText(value) === '');
}

function buildExistingRowsByIdentity(existingRows: IdsEcoaRow[]) {
  const existingRowsByIdentity = new Map<string, IdsEcoaRow>();

  existingRows.forEach((row) => {
    if (!normalizeText(row.idRegra) || parseMonthNumber(row.mesReferencia) === null) return;
    existingRowsByIdentity.set(getRowIdentityKey(row), row);
  });

  return existingRowsByIdentity;
}

function markRow(
  row: ParsedImportRow,
  status: ImportRowStatus,
  reason?: string
): ParsedImportRow {
  return {
    ...row,
    valid: status === 'valid',
    status,
    reason,
  };
}

function validateImportRows(rows: ParsedImportRow[], existingRows: IdsEcoaRow[] = []) {
  const firstRowByIdentity = new Map<string, ParsedImportRow>();
  const existingRowsByIdentity = buildExistingRowsByIdentity(existingRows);

  return rows.map((row) => {
    const nextRow: ParsedImportRow = {
      ...row,
      idRegra: normalizeText(row.idRegra),
      nomePromocao: normalizeText(row.nomePromocao),
      mesReferencia: finalizeMonthValue(row.mesReferencia),
      anoReferencia: normalizeText(row.anoReferencia) || finalizeYearValue(row.anoReferencia),
      valid: true,
      status: 'valid',
      reason: undefined,
    };

    if (isEmptyRow(nextRow)) {
      return markRow(nextRow, 'invalid', 'Linha vazia');
    }

    if (!nextRow.idRegra) {
      return markRow(nextRow, 'invalid', 'ID Regra vazio');
    }

    if (!nextRow.nomePromocao) {
      return markRow(nextRow, 'invalid', 'Nome Promoção vazio');
    }

    if (!nextRow.mesReferencia || parseMonthNumber(nextRow.mesReferencia) === null) {
      return markRow(nextRow, 'invalid', 'Mês Referência inválido');
    }

    if (parseYearNumber(nextRow.anoReferencia) === null) {
      return markRow(nextRow, 'invalid', 'Ano Referência inválido');
    }

    const key = getRowIdentityKey(nextRow);
    const firstRow = firstRowByIdentity.get(key);
    if (firstRow) {
      if (rowsAreEqual(nextRow, firstRow)) {
        return markRow(nextRow, 'skipped', `Duplicado sem alteração com a linha ${firstRow.rowNumber}`);
      }

      return markRow(nextRow, 'invalid', `Duplicado com a linha ${firstRow.rowNumber}`);
    }

    firstRowByIdentity.set(key, nextRow);

    const existingRow = existingRowsByIdentity.get(key);
    if (existingRow && rowsAreEqual(nextRow, existingRow)) {
      return markRow(nextRow, 'skipped', 'Já cadastrado sem alteração');
    }

    return markRow(nextRow, 'valid');
  });
}

function parseFile(file: File, existingRows: IdsEcoaRow[]): Promise<ParsedImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' });

        if (json.length < 2) {
          reject(new Error('A planilha deve conter cabeçalho e ao menos uma linha de dados.'));
          return;
        }

        const headers = json[0].map((value) => normalizeHeaderKey(value));
        const headerIndexes = new Map<string, number>();
        headers.forEach((header, index) => {
          if (header) headerIndexes.set(header, index);
        });

        const missingRequired = [
          { label: 'ID Regra', aliases: HEADER_ALIASES.idRegra },
          { label: 'Nome Promoção', aliases: HEADER_ALIASES.nomePromocao },
          { label: 'Mês Referência', aliases: HEADER_ALIASES.mesReferencia },
        ].filter((header) => getHeaderIndex(headerIndexes, header.aliases) === undefined);

        if (missingRequired.length > 0) {
          reject(new Error(`Cabeçalho inválido. Colunas obrigatórias ausentes: ${missingRequired.map((item) => item.label).join(' | ')}`));
          return;
        }

        const getValue = (row: any[], key: keyof Omit<IdsEcoaRow, 'guid'>) => {
          const index = getHeaderIndex(headerIndexes, HEADER_ALIASES[key]);
          return index === undefined ? '' : row[index];
        };

        const parsedRows = json.slice(1).map((row, index) => {
          const idRegra = getValue(row, 'idRegra');
          const nomePromocao = getValue(row, 'nomePromocao');
          const mesReferencia = getValue(row, 'mesReferencia');
          const anoReferencia = getValue(row, 'anoReferencia');
          const isRawEmpty = [idRegra, nomePromocao, mesReferencia, anoReferencia].every((value) => normalizeText(value) === '');

          if (isRawEmpty) return null;

          const item: ParsedImportRow = {
            guid: makeLocalGuid(),
            idRegra: normalizeText(idRegra),
            nomePromocao: normalizeText(nomePromocao),
            mesReferencia: finalizeMonthValue(mesReferencia),
            anoReferencia: normalizeText(anoReferencia) || finalizeYearValue(anoReferencia),
            rowNumber: index + 2,
            valid: true,
            status: 'valid',
          };

          return item;
        }).filter(Boolean) as ParsedImportRow[];

        resolve(validateImportRows(parsedRows, existingRows));
      } catch {
        reject(new Error('Não foi possível ler o arquivo XLSX.'));
      }
    };

    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

function stripParsedFields(row: ParsedImportRow): IdsEcoaRow {
  const { rowNumber, valid, status, reason, ...cleanRow } = row;
  return cleanRow;
}

function toSkippedRows(rows: ParsedImportRow[]): IdsEcoaSkippedRow[] {
  return rows.map((row) => ({
    row: row.rowNumber,
    idRegra: row.idRegra || '',
    nomePromocao: row.nomePromocao || '',
    mesReferencia: row.mesReferencia || '',
    anoReferencia: row.anoReferencia || '',
    reason: row.reason || 'Linha inválida',
  }));
}

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, existingRows, onClose, onImport }) => {
  const [visible, setVisible] = React.useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirmIgnoreOpen, setConfirmIgnoreOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = React.useCallback(() => {
    setFile(null);
    setRows([]);
    setParseError(null);
    setDragging(false);
    setImporting(false);
    setConfirmIgnoreOpen(false);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      reset();
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, reset]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !importing) {
        if (confirmIgnoreOpen) {
          setConfirmIgnoreOpen(false);
          return;
        }
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [confirmIgnoreOpen, isOpen, importing, onClose]);

  const validRows = rows.filter((row) => row.status === 'valid');
  const invalidRows = rows.filter((row) => row.status === 'invalid');
  const unchangedRows = rows.filter((row) => row.status === 'skipped');
  const ignoredRows = rows.filter((row) => row.status !== 'valid');
  const skippedRows = useMemo(() => toSkippedRows(ignoredRows), [ignoredRows]);

  const counts = useMemo(() => ({
    valid: validRows.length,
    invalid: invalidRows.length,
    ignored: ignoredRows.length,
    unchanged: unchangedRows.length,
    total: rows.length,
  }), [validRows.length, invalidRows.length, ignoredRows.length, unchangedRows.length, rows.length]);

  const handleFile = async (nextFile: File) => {
    setParseError(null);
    setRows([]);
    setConfirmIgnoreOpen(false);

    if (!nextFile.name.endsWith('.xlsx') && !nextFile.name.endsWith('.xls')) {
      setParseError('Formato inválido. Envie um arquivo XLSX ou XLS.');
      return;
    }

    setFile(nextFile);

    try {
      const parsedRows = await parseFile(nextFile, existingRows);
      setRows(parsedRows);
    } catch (error: any) {
      setParseError(error?.message || 'Não foi possível processar o arquivo.');
    }
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleEditRow = (
    guid: string,
    key: keyof Omit<IdsEcoaRow, 'guid'>,
    value: string
  ) => {
    setRows((currentRows) => validateImportRows(currentRows.map((row) => (
      row.guid === guid ? { ...row, [key]: value } : row
    )), existingRows));
  };

  const runImport = async (ignoredRows: IdsEcoaSkippedRow[]) => {
    if (validRows.length === 0 && ignoredRows.length === 0) return;
    setImporting(true);

    const success = await onImport(validRows.map(stripParsedFields), ignoredRows);

    setImporting(false);
    if (success) handleClose();
  };

  const handleImport = async () => {
    if (validRows.length === 0 && ignoredRows.length === 0) return;
    if (ignoredRows.length > 0) {
      setConfirmIgnoreOpen(true);
      return;
    }

    await runImport([]);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Importar IDs E-COA"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div
        className={`relative flex max-h-[90vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar IDs E-COA</h2>
            <p className="mt-0.5 text-xs text-gray-500">Revise, corrija ou importe apenas as linhas com alteração</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={importing}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs text-blue-800">
              <span className="font-semibold">Obrigatórias:</span>
              {REQUIRED_IMPORT_HEADERS.map((header) => (
                <span key={header} className="rounded-full bg-white px-2.5 py-1 font-medium shadow-sm">
                  {header}
                </span>
              ))}
            </div>
            <p className="mt-2 text-xs text-blue-700">
              Linhas inválidas podem ser corrigidas aqui mesmo. Itens já cadastrados sem alteração serão marcados como ignorados. Se o ano estiver vazio, ele será tratado como {new Date().getFullYear()}.
            </p>
          </div>

          {!file && (
            <div
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const droppedFile = event.dataTransfer.files[0];
                if (droppedFile) handleFile(droppedFile);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragging(false);
              }}
              onClick={() => inputRef.current?.click()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-colors ${
                dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
              }`}
              role="button"
            >
              <div className="mb-3 rounded-xl bg-white p-3 shadow-sm">
                <Upload className="h-7 w-7 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">Selecionar XLSX</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => {
                  const selectedFile = event.target.files?.[0];
                  if (selectedFile) handleFile(selectedFile);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                className="hidden"
              />
            </div>
          )}

          {parseError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="text-sm text-red-700">{parseError}</p>
              </div>
            </div>
          )}

          {file && rows.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-sm font-medium text-gray-700">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  disabled={importing}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remover
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <p className="text-lg font-bold text-gray-800">{counts.total}</p>
                  <p className="text-xs text-gray-500">Linhas lidas</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-lg font-bold text-emerald-700">{counts.valid}</p>
                  <p className="text-xs text-emerald-600">Com alteração</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-lg font-bold text-amber-700">{counts.ignored}</p>
                  <p className="text-xs text-amber-600">Ignoradas</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <p className="text-lg font-bold text-red-700">{counts.invalid}</p>
                  <p className="text-xs text-red-600">Inválidas</p>
                </div>
              </div>

              <div className="max-h-[390px] overflow-auto rounded-xl border border-gray-200">
                <table className="min-w-[1040px] w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Linha</th>
                      {EXPECTED_HEADERS.map((header) => (
                        <th key={header} className="px-3 py-2 text-left font-semibold text-gray-500">
                          {header}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((row) => (
                      <tr
                        key={`${row.rowNumber}-${row.guid}`}
                        className={
                          row.status === 'invalid'
                            ? 'bg-red-50/60'
                            : row.status === 'skipped'
                              ? 'bg-amber-50/60'
                              : ''
                        }
                      >
                        <td className="px-3 py-2 font-mono text-gray-500">{row.rowNumber}</td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.idRegra || ''}
                            onChange={(event) => handleEditRow(row.guid, 'idRegra', event.target.value)}
                            className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 font-mono text-xs text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                          />
                        </td>
                        <td className="min-w-[360px] px-3 py-2">
                          <input
                            type="text"
                            value={row.nomePromocao || ''}
                            onChange={(event) => handleEditRow(row.guid, 'nomePromocao', event.target.value)}
                            className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={getMonthSelectValue(row.mesReferencia)}
                            onChange={(event) => handleEditRow(row.guid, 'mesReferencia', event.target.value)}
                            className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="">-</option>
                            {MONTH_OPTIONS.map((month) => (
                              <option key={month.value} value={month.value}>{month.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={getYearSelectValue(row.anoReferencia)}
                            onChange={(event) => handleEditRow(row.guid, 'anoReferencia', event.target.value)}
                            className="h-9 w-full rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-800 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
                          >
                            {YEAR_OPTIONS.map((year) => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium ${
                            row.status === 'valid'
                              ? 'bg-emerald-50 text-emerald-700'
                              : row.status === 'skipped'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-red-100 text-red-700'
                          }`}>
                            {row.status === 'valid' ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            {row.status === 'valid' ? 'Com alteração' : row.reason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={importing}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={(validRows.length === 0 && ignoredRows.length === 0) || importing}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {ignoredRows.length > 0 ? (validRows.length > 0 ? 'Importar com ignoradas' : 'Registrar ignoradas') : 'Importar'}
          </button>
        </div>
      </div>

      {confirmIgnoreOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Confirmar importação com linhas ignoradas"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmIgnoreOpen(false)} />
          <div className="relative w-full max-w-xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start gap-4 p-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50">
                <HelpCircle className="h-7 w-7 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4">
                  <h2 className="text-lg font-semibold text-gray-900">Importar ignorando linhas?</h2>
                  <button
                    type="button"
                    onClick={() => setConfirmIgnoreOpen(false)}
                    disabled={importing}
                    className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Fechar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {validRows.length} {validRows.length === 1 ? 'linha com alteração será importada' : 'linhas com alteração serão importadas'} e {ignoredRows.length} {ignoredRows.length === 1 ? 'linha será ignorada' : 'linhas serão ignoradas'}.
                  {unchangedRows.length > 0 && ` ${unchangedRows.length} ${unchangedRows.length === 1 ? 'já está cadastrada sem alteração' : 'já estão cadastradas sem alteração'}.`}
                  {invalidRows.length > 0 && ` ${invalidRows.length} ${invalidRows.length === 1 ? 'está inválida' : 'estão inválidas'}.`}
                </p>
                <div className="mt-4 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <div className="space-y-2">
                    {skippedRows.map((row) => (
                      <p key={`${row.row}-${row.idRegra}-${row.reason}`} className="text-xs leading-5 text-gray-700">
                        Linha {row.row}: {row.idRegra || '-'} - {row.reason}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                onClick={() => setConfirmIgnoreOpen(false)}
                disabled={importing}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                Corrigir no modal
              </button>
              <button
                type="button"
                onClick={() => runImport(skippedRows)}
                disabled={importing}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                Avançar ignorando
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
